import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Collapse,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Paper,
    TextField,
    Typography,
    styled,
} from '@mui/material';
import Header from './Header';
import Footer from './Footer';
import { createDemoRequest } from '../api/auth';

const PAIN_FIELDS = [
    { key: 'visibility', label: 'No real visibility over what’s happening in the field' },
    { key: 'sessionVerification', label: 'Session and visit verification' },
    { key: 'documentation', label: 'Documentation and records' },
    { key: 'payoutDisputes', label: 'Therapist payout disputes' },
];

const initialPains = {
    visibility: false,
    sessionVerification: false,
    documentation: false,
    payoutDisputes: false,
};

const DemoBookingPage = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: '',
        phone: '',
        centreName: '',
        city: '',
        therapistCount: '',
    });
    const [pains, setPains] = useState(initialPains);
    const [touched, setTouched] = useState({});
    const [thankYouOpen, setThankYouOpen] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const errors = useMemo(() => {
        const next = {};
        if (!form.name.trim()) next.name = 'Name is required';
        if (!form.phone.trim()) next.phone = 'Phone / WhatsApp number is required';
        if (!form.centreName.trim()) next.centreName = 'Centre name is required';
        if (!form.city.trim()) next.city = 'City is required';
        if (!form.therapistCount.trim()) next.therapistCount = 'Number of therapists is required';

        const painOk =
            pains.visibility ||
            pains.sessionVerification ||
            pains.documentation ||
            pains.payoutDisputes;
        if (!painOk) next.pains = 'Please select at least one option';

        return next;
    }, [form, pains]);

    const isValid = Object.keys(errors).length === 0;

    const onChange = (key) => (e) => {
        setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

    const onTherapistCountChange = (e) => {
        const digitsOnly = e.target.value.replace(/\D/g, '');
        setForm((prev) => ({ ...prev, therapistCount: digitsOnly }));
    };

    const onBlur = (key) => () => {
        setTouched((prev) => ({ ...prev, [key]: true }));
    };

    const onTogglePain = (key) => (e) => {
        setTouched((t) => ({ ...t, pains: true }));
        const checked = e.target.checked;
        setPains((prev) => ({ ...prev, [key]: checked }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');
        setTouched({
            name: true,
            phone: true,
            centreName: true,
            city: true,
            therapistCount: true,
            pains: true,
        });

        if (!isValid) return;

        const painPoints = PAIN_FIELDS.filter(({ key }) => pains[key]).map(({ key }) => key);
        setSubmitLoading(true);
        try {
            await createDemoRequest({
                name: form.name.trim(),
                phone: form.phone.trim(),
                centreName: form.centreName.trim(),
                city: form.city.trim(),
                therapistCount: form.therapistCount.trim(),
                painPoints,
            });
            setThankYouOpen(true);
            setForm({
                name: '',
                phone: '',
                centreName: '',
                city: '',
                therapistCount: '',
            });
            setPains(initialPains);
            setTouched({});
        } catch (err) {
            setSubmitError(err.message || 'Could not submit demo request');
        } finally {
            setSubmitLoading(false);
        }
    };

    const painTouched = Boolean(touched.pains);
    const painError = painTouched && errors.pains;

    return (
        <>
            <Header />
            <Box component="main" sx={{ pt: { xs: 10, md: 12 }, pb: { xs: 8, md: 10 } }}>
                <Container maxWidth="lg">
                    <HeroWrap>
                        <HeroTitle variant="h3" component="h1">
                            30 minutes to see if this fixes what's breaking
                        </HeroTitle>
                        <HeroSubtitle>
                            No generic walkthrough. We'll show you how it works for a centre your size: session
                            verification, records, and payouts without the disputes.
                        </HeroSubtitle>
                        <BodyCopy>
                            Five rehab centres are already running on meDZhub. Every one of them came in with the same
                            problems: no real visibility, messy records, and payout disputes every month. Tell us a
                            little about your setup and we'll make the demo relevant to how your centre runs today.
                        </BodyCopy>
                    </HeroWrap>

                    <FormWrap component="section" aria-label="Book a demo form">

                        <FormPaper elevation={0}>
                            <Box component="form" onSubmit={handleSubmit} noValidate>
                                <FormGrid>
                                    <TextField
                                        label="Name"
                                        value={form.name}
                                        onChange={onChange('name')}
                                        onBlur={onBlur('name')}
                                        error={Boolean(touched.name && errors.name)}
                                        helperText={touched.name && errors.name ? errors.name : ' '}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Phone / WhatsApp number"
                                        value={form.phone}
                                        onChange={onChange('phone')}
                                        onBlur={onBlur('phone')}
                                        error={Boolean(touched.phone && errors.phone)}
                                        helperText={touched.phone && errors.phone ? errors.phone : ' '}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Centre name"
                                        value={form.centreName}
                                        onChange={onChange('centreName')}
                                        onBlur={onBlur('centreName')}
                                        error={Boolean(touched.centreName && errors.centreName)}
                                        helperText={touched.centreName && errors.centreName ? errors.centreName : ' '}
                                        fullWidth
                                    />
                                    <TextField
                                        label="City"
                                        value={form.city}
                                        onChange={onChange('city')}
                                        onBlur={onBlur('city')}
                                        error={Boolean(touched.city && errors.city)}
                                        helperText={touched.city && errors.city ? errors.city : ' '}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Number of therapists"
                                        value={form.therapistCount}
                                        onChange={onTherapistCountChange}
                                        onBlur={onBlur('therapistCount')}
                                        error={Boolean(touched.therapistCount && errors.therapistCount)}
                                        helperText={
                                            touched.therapistCount && errors.therapistCount
                                                ? errors.therapistCount
                                                : ' '
                                        }
                                        fullWidth
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', autoComplete: 'off' }}
                                    />
                                </FormGrid>

                                <FormControl
                                    component="fieldset"
                                    variant="standard"
                                    error={Boolean(painError)}
                                    sx={{ mt: 1, width: '100%' }}
                                >
                                    <FormLabel component="legend" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
                                        What's breaking in your operation?
                                    </FormLabel>
                                    <FormGroup>
                                        {PAIN_FIELDS.map(({ key, label }) => (
                                            <FormControlLabel
                                                key={key}
                                                control={
                                                    <Checkbox
                                                        checked={pains[key]}
                                                        onChange={onTogglePain(key)}
                                                        sx={{ color: '#0066FF', '&.Mui-checked': { color: '#0066FF' } }}
                                                    />
                                                }
                                                label={label}
                                                sx={{ alignItems: 'flex-start', ml: 0, '& .MuiCheckbox-root': { pt: 0.25 } }}
                                            />
                                        ))}
                                    </FormGroup>
                                    {painError ? <FormHelperText sx={{ mx: 0 }}>{errors.pains}</FormHelperText> : null}
                                </FormControl>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={submitLoading}
                                            sx={{
                                                borderRadius: 999,
                                                textTransform: 'none',
                                                fontWeight: 800,
                                                px: 3,
                                                py: 1.25,
                                                bgcolor: '#0066FF',
                                                '&:hover': { bgcolor: '#0057DB' },
                                            }}
                                        >
                                            {submitLoading ? 'Sending…' : 'Send my request'}
                                        </Button>
                                    </Box>
                                    <Collapse in={Boolean(submitError)}>
                                        <Alert severity="error" onClose={() => setSubmitError('')} sx={{ borderRadius: 2 }}>
                                            {submitError}
                                        </Alert>
                                    </Collapse>
                                </Box>
                            </Box>
                        </FormPaper>
                    </FormWrap>
                </Container>
            </Box>
            <Footer />

            <Dialog
                open={thankYouOpen}
                onClose={() => setThankYouOpen(false)}
                slotProps={{
                    paper: { sx: { borderRadius: 3, maxWidth: 420 } },
                }}
            >
                <DialogTitle sx={{ fontWeight: 900, letterSpacing: '-0.02em', pt: 3, px: 3 }}>
                    Thanks - request received
                </DialogTitle>
                <DialogContent sx={{ px: 3, pb: 2 }}>
                    <Typography sx={{ color: '#475569', fontWeight: 600, lineHeight: 1.65 }}>
                        We'll reach out on WhatsApp within one working day to set up a time.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => {
                            setThankYouOpen(false);
                            navigate('/');
                        }}
                        variant="contained"
                        sx={{
                            borderRadius: 999,
                            textTransform: 'none',
                            fontWeight: 800,
                            bgcolor: '#0066FF',
                            '&:hover': { bgcolor: '#0057DB' },
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const HeroWrap = styled(Box)({
    textAlign: 'left',
    maxWidth: 900,
    marginBottom: 28,
});

const HeroTitle = styled(Typography)({
    fontWeight: 900,
    letterSpacing: '-0.03em',
    color: '#0F172A',
    marginBottom: 12,
    lineHeight: 1.15,
});

const HeroSubtitle = styled(Typography)({
    fontSize: 18,
    color: '#475569',
    fontWeight: 600,
    lineHeight: 1.65,
    marginBottom: 14,
});

const BodyCopy = styled(Typography)({
    fontSize: 17,
    color: '#64748B',
    fontWeight: 500,
    lineHeight: 1.75,
});

const FormWrap = styled(Box)({
    marginTop: 6,
});

const FormPaper = styled(Paper)({
    borderRadius: 18,
    padding: '22px 18px',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
});

const FormGrid = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
    '@media (max-width: 900px)': {
        gridTemplateColumns: '1fr',
    },
});

export default DemoBookingPage;
